import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAdminStatsAction } from "../../redux/actions/adminActions";
import usePageMeta from "../../hooks/usePageMeta";

const StatCard = ({ label, value, icon, to, color }) => (
    <Link to={to} className="block">
        <div className={`bg-white rounded-2xl shadow-1 p-[2vmax] flex items-center gap-[1.5vmax] hover:shadow-lg transition-shadow`}>
            <div className={`w-[5vmax] h-[5vmax] rounded-xl flex-center text-[2vmax] text-white ${color}`}>
                <i className={`fa-solid ${icon}`} />
            </div>
            <div>
                <p className="text-[0.9vmax] text-slate-500 uppercase tracking-wide">{label}</p>
                <h2 className="text-[2.5vmax] font-bold font-2">{value ?? "—"}</h2>
            </div>
        </div>
    </Link>
);

const AdminDashboard = () => {
    const dispatch = useDispatch();
    const { totalStores, totalUsers, totalPriceBook, loading } = useSelector(s => s.adminStats);

    usePageMeta("adminDashboard");

    useEffect(() => {
        dispatch(getAdminStatsAction());
    }, [dispatch]);

    return (
        <div className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax]">
            <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax] mb-[2vmax]">
                <h2 className="text-[1.3vmax] font-bold">Admin Dashboard</h2>
                <span className="text-[1vmax] text-slate-400">Overview</span>
            </div>

            {loading ? (
                <p className="text-center text-slate-400 py-[4vmax]">Loading...</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-[1.5vmax]">
                    <StatCard
                        label="Total Stores"
                        value={totalStores}
                        icon="fa-store"
                        to="/admin/stores"
                        color="bg-[var(--color-2)]"
                    />
                    <StatCard
                        label="Total Users"
                        value={totalUsers}
                        icon="fa-users"
                        to="/admin/users"
                        color="bg-green-500"
                    />
                    <StatCard
                        label="Price Book Items"
                        value={totalPriceBook}
                        icon="fa-book"
                        to="/admin/barcode-mapping"
                        color="bg-purple-500"
                    />
                </div>
            )}

            <div className="mt-[2vmax] grid grid-cols-1 md:grid-cols-3 gap-[1.5vmax]">
                {[
                    { label: "Analytics & Forecasting", desc: "Cross-store insights, demand forecasts, reorder alerts", to: "/admin/analytics", icon: "fa-chart-pie" },
                    { label: "Manage Stores", desc: "Create, edit, and delete stores", to: "/admin/stores", icon: "fa-store" },
                    { label: "Manage Users", desc: "Create users and set menu permissions", to: "/admin/users", icon: "fa-users" },
                    { label: "Master Price Book", desc: "Manage global UPC price catalog", to: "/admin/barcode-mapping", icon: "fa-book" },
                ].map(({ label, desc, to, icon }) => (
                    <Link key={to} to={to}
                        className="bg-white rounded-2xl shadow-1 p-[2vmax] hover:shadow-lg transition-shadow flex flex-col gap-[0.8vmax]"
                    >
                        <i className={`fa-solid ${icon} text-[var(--color-2)] text-[2vmax]`} />
                        <h3 className="font-bold text-[1.2vmax]">{label}</h3>
                        <p className="text-[1vmax] text-slate-500">{desc}</p>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default AdminDashboard;
